import { IOverflowSetItemProps, IButtonStyles, TooltipHost, IconButton } from '@fluentui/react';
import { useMemo } from 'react';
import { useUISettings } from '../../../../../../../../../context/network/http/QueryProvider/queries/UISettings';
import { UserType } from '../../../../../../../../../api/generated/data-contracts';
import { useStore } from '../../../../../../../../../context/store';
import { useCheckTPPermissions } from '../../../../../../../../../hooks/usePermissions';
import { ApiCalls } from '../../../../../../../../../api/api';
import { useGridRowContext } from '../../../../../../../_grid/contexts/rowContext';
import { useRecoilState } from 'recoil';
import { rowMetadataAtoms } from '../../../../../../../_grid/rowState';
import shallow from 'zustand/shallow';
import { useTranslation } from '../../../../../../../../../Locales/i18n/localizationService';

export const AlignContractMenu = () => {
    const { t } = useTranslation();
    const { row } = useGridRowContext();
    const contractId = row.identity.id;
    const { data: uiSettings } = useUISettings();
    const [rowMetaData, setRowMetaData] = useRecoilState(rowMetadataAtoms(row));

	const status = useMemo(() => {
		if (!uiSettings.settings.enableAlignContract || !uiSettings.settings.alignContractStatuses?.length) {
			return null;
        }
        return (
			uiSettings.settings.alignContractStatuses.find(_ => _.id === rowMetaData?.Status) ||
            [...(uiSettings.settings.alignContractStatuses || [])].sort((a, b) => (a.order > b.order ? 1 : -1))[0]
        );
    }, [rowMetaData?.Status, uiSettings.settings.alignContractStatuses, uiSettings.settings.enableAlignContract]);

    const { error, success } = useStore(store => ({
        error: store.addErrorNotification,
        success: store.addSuccesNotification,
    }), shallow);
    const checkPermission = useCheckTPPermissions({ some: true });

    const buttonStyles = useMemo((): Partial<IButtonStyles> => {
        if (!status) {
            return {};
        }
        return {
            menuIcon: { color: status.color },
            menuIconHovered: { color: status.color },
            menuIconExpandedHovered: { color: status.color },
        };
    }, [status]);

    const overflowItems = useMemo((): IOverflowSetItemProps[] => {
        return [
            {
                key: 'header',
                name: t('AlignContractMenu.ChangeStatus'),
                disabled: true,
                style: { color: 'black', fontWeight: 'bold', backgroundColor: '#fafafa' },
            },
            ...uiSettings.settings.alignContractStatuses
                .toSorted((a, b) => (a.order > b.order ? 1 : -1))
                .map((alignStatus, idx) => {
                    const isPM = checkPermission({ userTypes: [UserType.ProjectOwner] });
                    const isLM = checkPermission({ userTypes: [UserType.LineManager] });
                    const isSU = checkPermission({ userTypes: [UserType.SuperUser] });

                    let disableControl: boolean = false;

                    if (isPM && isLM && !isSU) {
                        disableControl = !alignStatus.editableBy.some((ut: UserType) => {
                            return ut === UserType.LineManager;
                        });
                    }

                    const isAllowed = checkPermission({ userTypes: [...alignStatus.editableBy] }) && !disableControl;
                    return {
                        key: `${alignStatus.id}_header_${idx}`,
                        name: alignStatus.name,
                        disabled: !isAllowed,
                        iconProps: { iconName: isAllowed ? 'FullCircleMask' : 'Lock', style: { color: alignStatus?.color } },

                        onClick: () => {
                            const prevStatus = status;
                            setRowMetaData(data => {
                                return { ...data, Status: alignStatus.id };
                            });
                            // update contract
                            if (prevStatus.id === alignStatus.id) return;
                            ApiCalls.updateContractAlignStatus({ contractId: contractId, newStatus: alignStatus.id })
                                .then(() => {
                                    if (alignStatus.customNotification) {
                                        success(alignStatus.customNotification);
                                    }
                                })
                                .catch(() => {
                                    error(t('AlignContractMenu.FailedChangingContractAlignment'));
                                });
                        },
                    } as IOverflowSetItemProps;
                }),
        ];
    }, [uiSettings.settings.alignContractStatuses, checkPermission, status, setRowMetaData, contractId, success, error, t]);

	if (!uiSettings.settings.enableAlignContract) {
		return null
	}

    return (
        <TooltipHost
            content={
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ height: 10, width: 10, backgroundColor: status?.color, borderRadius: '50%' }} />
                    {status?.name}
                </span>
            }
            calloutProps={{ gapSpace: 0, isBeakVisible: true }}
        >
            <IconButton
                ariaLabel={t('AlignContractMenu.AlignStatus')}
                role="menuitem"
                styles={buttonStyles}
                menuIconProps={{ iconName: 'FullCircleMask' }}
                menuProps={{
                    items: overflowItems!,
                }}
            />
        </TooltipHost>
    );
};
